/* Section-scoped Swiper lifecycle for the Shopify hero banner. */
(() => {
  'use strict';

  const moduleKey = Symbol.for('seldravinpack.heroBanner.initialize');
  if (window[moduleKey]) {
    window[moduleKey](document);
    return;
  }

  const instances = new Map();
  const escapeAttribute = value => String(value).replace(/[&<>"']/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[character]));
  const numberOr = (value, fallback, minimum = 0) => Number.isFinite(Number(value)) && Number(value) >= minimum ? Number(value) : fallback;

  function createBanner(root) {
    const banner = root.querySelector('.hero-swiper');
    const originalSlides = [...(banner?.querySelectorAll('.swiper-wrapper > .swiper-slide') || [])];
    if (!banner || !originalSlides.length || typeof window.Swiper !== 'function') return null;

    let config = {};
    try { config = JSON.parse(root.querySelector('[data-hero-config]')?.textContent || '{}'); }
    catch { /* An incomplete editor update can use the original banner defaults. */ }
    const slidesPerView = numberOr(config.slidesPerView, 1, 1);
    const spaceBetween = numberOr(config.spaceBetween, 0);
    const breakpoints = {};
    Object.entries(config.breakpoints || {}).forEach(([width, values]) => {
      if (!Number.isFinite(Number(width)) || Number(width) < 0 || !values) return;
      breakpoints[width] = {
        slidesPerView: numberOr(values.slidesPerView, slidesPerView, 1),
        spaceBetween: numberOr(values.spaceBetween, spaceBetween)
      };
    });
    const largestView = Math.max(slidesPerView, ...Object.values(breakpoints).map(value => value.slidesPerView));
    const multipleVisible = largestView > 1;
    // Swiper 11 requires one extra slide beyond the rounded-up visible count.
    const canLoop = config.loop === true && originalSlides.length >= Math.ceil(largestView) + 1;
    const canAutoplay = originalSlides.length > 1;
    const messages = config.messages || {};
    const goToSlide = messages.goToSlide || 'Go to banner {index}';
    const previous = root.querySelector('.banner-prev');
    const next = root.querySelector('.banner-next');
    const pagination = root.querySelector('.banner-pagination');
    const controls = [previous, next, pagination].filter(Boolean).map(element => ({ element, hidden: element.hidden }));
    const slideAttributes = originalSlides.map(slide => ({ slide, inert: slide.inert, hidden: slide.getAttribute('aria-hidden') }));
    const controller = new AbortController();
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    let hovered = false;
    let selectedBlock = null;
    let focusTimer;
    let destroyed = false;
    let slider = null;

    const listen = (target, type, handler) => target?.addEventListener(type, handler, { signal: controller.signal });

    function syncSlides(swiper) {
      if (destroyed || !swiper.slides) return;
      const visible = new Set(swiper.visibleSlides || []);
      const visibleCount = Math.ceil(Number(swiper.params.slidesPerView) || 1);
      swiper.slides.forEach((slide, index) => {
        const isVisible = multipleVisible
          ? (visible.size ? visible.has(slide) : index >= swiper.activeIndex && index < swiper.activeIndex + visibleCount)
          : index === swiper.activeIndex;
        slide.inert = !isVisible;
        slide.setAttribute('aria-hidden', String(!isVisible));
      });
    }

    function syncAutoplay() {
      if (destroyed || !slider?.autoplay) return;
      const shouldPlay = canAutoplay && !slider.isLocked && !reducedMotion.matches && !hovered
        && !root.contains(document.activeElement) && !document.hidden && !selectedBlock;
      if (shouldPlay) {
        if (!slider.autoplay.running) slider.autoplay.start();
      } else if (slider.autoplay.running) {
        slider.autoplay.stop();
      }
    }

    controls.forEach(({ element }) => { if (!canAutoplay) element.hidden = true; });
    slider = new window.Swiper(banner, {
      loop: canLoop,
      slidesPerView,
      spaceBetween,
      breakpoints,
      effect: multipleVisible ? 'slide' : 'fade',
      fadeEffect: { crossFade: true },
      speed: reducedMotion.matches ? 0 : 550,
      watchOverflow: true,
      watchSlidesProgress: true,
      autoplay: canAutoplay ? { delay: numberOr(config.delay, 5500, 1), disableOnInteraction: false } : false,
      navigation: { nextEl: next, prevEl: previous },
      pagination: {
        el: pagination,
        clickable: true,
        renderBullet(index, className) {
          const label = goToSlide.replace(/\{index\}/g, String(index + 1));
          return `<button type="button" class="${className}" aria-label="${escapeAttribute(label)}"></button>`;
        }
      },
      a11y: {
        containerMessage: messages.label || 'Featured packaging',
        prevSlideMessage: messages.previous || 'Previous banner',
        nextSlideMessage: messages.next || 'Next banner',
        paginationBulletMessage: goToSlide.replace(/\{index\}/g, '{{index}}')
      },
      on: {
        init: syncSlides,
        setTranslate: syncSlides,
        slideChange: syncSlides,
        transitionEnd: syncSlides,
        resize(swiper) { syncSlides(swiper); syncAutoplay(); },
        breakpoint(swiper) { syncSlides(swiper); syncAutoplay(); },
        lock: syncAutoplay,
        unlock: syncAutoplay
      }
    });

    listen(root, 'pointerenter', event => {
      if (event.pointerType === 'mouse' || event.pointerType === 'pen') {
        hovered = true;
        syncAutoplay();
      }
    });
    listen(root, 'pointerleave', () => { hovered = false; syncAutoplay(); });
    listen(root, 'focusin', syncAutoplay);
    listen(root, 'focusout', () => {
      clearTimeout(focusTimer);
      focusTimer = setTimeout(syncAutoplay, 0);
    });
    listen(document, 'visibilitychange', syncAutoplay);
    listen(reducedMotion, 'change', () => {
      slider.params.speed = reducedMotion.matches ? 0 : 550;
      slider.originalParams.speed = slider.params.speed;
      syncAutoplay();
    });
    listen(root, 'shopify:block:select', event => {
      const blockId = String(event.detail?.blockId || event.target.closest('[data-block-id]')?.dataset.blockId || '');
      const index = originalSlides.findIndex(slide => slide.dataset.blockId === blockId || slide.contains(event.target));
      if (index < 0) return;
      selectedBlock = blockId || originalSlides[index].dataset.blockId || String(index);
      syncAutoplay();
      if (slider.params.loop) slider.slideToLoop(index, 0);
      else slider.slideTo(index, 0);
      syncSlides(slider);
    });
    listen(root, 'shopify:block:deselect', event => {
      if (!selectedBlock) return;
      const blockId = String(event.detail?.blockId || event.target.closest('[data-block-id]')?.dataset.blockId || '');
      if (!blockId || blockId === selectedBlock) {
        selectedBlock = null;
        syncAutoplay();
      }
    });
    syncSlides(slider);
    syncAutoplay();

    return {
      destroy() {
        destroyed = true;
        clearTimeout(focusTimer);
        controller.abort();
        slider.autoplay?.stop();
        slider.destroy(true, true);
        slideAttributes.forEach(({ slide, inert, hidden }) => {
          slide.inert = inert;
          if (hidden === null) slide.removeAttribute('aria-hidden');
          else slide.setAttribute('aria-hidden', hidden);
        });
        controls.forEach(({ element, hidden }) => { element.hidden = hidden; });
      }
    };
  }

  function initialize(scope) {
    const roots = [...scope.querySelectorAll('[data-hero-banner]')];
    if (scope.matches?.('[data-hero-banner]')) roots.unshift(scope);
    roots.forEach(root => {
      if (instances.has(root)) return;
      const instance = createBanner(root);
      if (instance) instances.set(root, instance);
    });
  }

  function removeBanner(root) {
    instances.get(root)?.destroy();
    instances.delete(root);
  }

  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    [...instances.keys()].forEach(root => {
      if (event.target === root || event.target.contains(root)) removeBanner(root);
    });
  });
  new MutationObserver(() => {
    [...instances.keys()].forEach(root => { if (!root.isConnected) removeBanner(root); });
  }).observe(document.documentElement, { childList: true, subtree: true });

  window[moduleKey] = initialize;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
  window.addEventListener('load', () => initialize(document), { once: true });
})();
